import { router } from "@inertiajs/react";
import { useCallback } from "react";
import type { SortDescriptor } from "react-aria-components";

interface UseAdminListNavigationOptions<F extends Record<string, string>> {
  basePath: string;
  filters?: F;
  sort?: string | null;
  sortDir?: string | null;
  filterToRansack?: Record<keyof F, string>;
}

interface NavigationApi<F extends Record<string, string>> {
  navigate: (next: { filters?: F; sort?: string | null; sortDir?: string | null; page?: number }) => void;
  handleSortChange: (descriptor: SortDescriptor) => void;
  handlePageChange: (nextPage: number) => void;
  handleSearchChange: (value: string) => void;
  handleFilterChange: (key: keyof F, value: string) => void;
}

export function useAdminListNavigation<F extends Record<string, string>>({
  basePath,
  filters = {} as F,
  sort = null,
  sortDir = null,
  filterToRansack = {} as Record<keyof F, string>,
}: UseAdminListNavigationOptions<F>): NavigationApi<F> {
  const buildQuery = useCallback(
    ({
      filters: nextFilters,
      sort: nextSort,
      sortDir: nextSortDir,
      page,
    }: {
      filters: F;
      sort: string | null;
      sortDir: string | null;
      page: number;
    }) => {
      const q: Record<string, string> = {};
      for (const key of Object.keys(filterToRansack) as Array<keyof F>) {
        const value = nextFilters[key];
        const ransackKey = filterToRansack[key];
        if (typeof value === "string" && value !== "" && ransackKey) q[ransackKey] = value;
      }
      if (nextSort && nextSortDir) q.s = `${nextSort} ${nextSortDir}`;
      return { q, page };
    },
    [filterToRansack],
  );

  const navigate = useCallback(
    (next: { filters?: F; sort?: string | null; sortDir?: string | null; page?: number }) => {
      const query = buildQuery({
        filters: next.filters ?? filters,
        sort: next.sort !== undefined ? next.sort : sort,
        sortDir: next.sortDir !== undefined ? next.sortDir : sortDir,
        page: next.page ?? 1,
      });
      router.get(basePath, query, { preserveState: true, preserveScroll: true });
    },
    [basePath, buildQuery, filters, sort, sortDir],
  );

  const handleSortChange = useCallback(
    (descriptor: SortDescriptor) => {
      const field = String(descriptor.column);
      const dir = descriptor.direction === "ascending" ? "asc" : "desc";
      navigate({ sort: field, sortDir: dir, page: 1 });
    },
    [navigate],
  );

  const handlePageChange = useCallback((nextPage: number) => navigate({ page: nextPage }), [navigate]);

  const handleSearchChange = useCallback(
    (value: string) => navigate({ filters: { ...filters, search: value } as F, page: 1 }),
    [filters, navigate],
  );

  const handleFilterChange = useCallback(
    (key: keyof F, value: string) => navigate({ filters: { ...filters, [key]: value } as F, page: 1 }),
    [filters, navigate],
  );

  return { navigate, handleSortChange, handlePageChange, handleSearchChange, handleFilterChange };
}
